import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../../../components/ui/button';
import { breachReportsAPI, entitiesAPI } from '../../../services/api';
import api from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import { useTheme } from '../../../contexts/ThemeContext';
import { WorkflowHeader } from '../../../components/shared';
import { AutoTaskPromptModal } from '../../../components/shared/AutoTaskPromptModal';
import { TasksSection } from '../../../components/shared/TasksSection';
import { FormStepper, StepperProgress } from '../../../components/ui/form-stepper';
import { ArrowLeft, ArrowRight, Save, Clock, FileDown } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../../lib/utils';

import {
  STEPS, INITIAL_FORM_DATA, MAX_POINTS,
  getRiskBand, rawToLevel,
  computePointsBreakdown, normalisePoints, volumeScore, containmentScore,
  JURISDICTION_BREACH_RULES, getNotificationRequirement, getJurisdictionRules
} from './constants';
import { validateStep, getFirstErrorFieldRef } from './utils/validation';
import { exportAPI } from '../../../services/api';

// Step components
import { Step1InitialReport } from './components';
import Step2Classification from './steps/Step2Classification';
import Step3RootCause from './steps/Step3RootCause';
import Step4DataAffected from './steps/Step4DataAffected';
import Step5Response from './steps/Step5Response';
import Step6RiskAssessment from './steps/Step6RiskAssessment';
import Step7Notifications from './steps/Step7Notifications';
import Step8Remediation from './steps/Step8Remediation';
import Step9Review from './steps/Step9Review';

// ── Breach Ownership Status (shows DPO decision context) ─────────────────────
const BreachOwnershipStatus = ({ dpoDecision, isDpoRole }) => {
  if (dpoDecision === 'report') {
    return (
      <p className="text-sm text-right text-muted-foreground">
        Notification to regulator in progress. Deadline tracked above.
      </p>
    );
  }
  if (dpoDecision === 'no_report') {
    return (
      <p className="text-sm text-right text-muted-foreground">
        DPO has determined notification is not required. Rationale recorded in Step 7.
      </p>
    );
  }
  if (dpoDecision === 'not_a_breach') {
    return (
      <p className="text-sm text-right text-muted-foreground">
        DPO has determined this is not a personal data breach.
      </p>
    );
  }
  // pending or empty
  if (isDpoRole) {
    return (
      <p className="text-sm text-right text-muted-foreground">
        Notification decision required. Review Step 7.
      </p>
    );
  }
  return (
    <p className="text-sm text-right text-muted-foreground">
      Your DPO has been notified and is reviewing this incident.
    </p>
  );
};

// ── Countdown Timer (kept here since it's used in the header bar) ───────────
const CountdownTimer = ({ awarenessDate, confirmedDeadline, jurisdiction, deadlineDays, isDpoRole }) => {
  const [timeLeft, setTimeLeft] = useState(null);
  useEffect(() => {
    let deadline = null;
    if (confirmedDeadline) deadline = new Date(confirmedDeadline + 'T23:59:59');
    else if (awarenessDate && deadlineDays) { deadline = new Date(awarenessDate); deadline.setDate(deadline.getDate() + deadlineDays); }
    if (!deadline) { setTimeLeft(null); return; }
    const tick = () => setTimeLeft(deadline - new Date());
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [awarenessDate, confirmedDeadline, deadlineDays]);

  if (timeLeft === null) {
    const rules = JURISDICTION_BREACH_RULES[jurisdiction];
    const deadlineText = rules?.deadlineDays ? `${rules.deadlineDays} day${rules.deadlineDays > 1 ? 's' : ''}` : 'TBD';
    return (<div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-muted text-muted-foreground text-sm"><Clock className="h-4 w-4" /><span className="font-mono">Deadline: {deadlineText}</span></div>);
  }
  const isOverdue = timeLeft < 0;
  const absMs = Math.abs(timeLeft);
  const days = Math.floor(absMs / 86400000);
  const h = Math.floor((absMs % 86400000) / 3600000);
  const m = Math.floor((absMs % 3600000) / 60000);
  const s = Math.floor((absMs % 60000) / 1000);
  const timeStr = days > 0 ? `${days}d ${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}` : `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`;
  let bg = 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400';
  if (isOverdue) bg = isDpoRole ? 'bg-red-200 text-red-900 dark:bg-red-900/50 dark:text-red-300 animate-pulse' : 'bg-red-200 text-red-900 dark:bg-red-900/50 dark:text-red-300';
  else if (timeLeft < 86400000) bg = isDpoRole ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400 animate-pulse' : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
  else if (timeLeft < 172800000) bg = 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400';
  return (<div data-testid="countdown-timer" className={cn("flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium", bg)}><Clock className="h-4 w-4" /><span className="font-mono">{isOverdue ? 'OVERDUE' : 'Deadline'} {timeStr}</span></div>);
};

// ── Risk Variables constant ─────────────────────────────────────────────────
const RISK_VARIABLES = [
  { id: 'data_sensitivity', name: 'Data Sensitivity', ticks: ['Basic', 'Personal', 'Sensitive', 'Special Cat.', 'Critical'], inverse: false },
  { id: 'volume_of_records', name: 'Volume of Records', ticks: ['1-10', '10-100', '100-1K', '1K-10K', '10K+'], inverse: false },
  { id: 'ease_of_identification', name: 'Ease of Identification', ticks: ['Anonymised', 'Pseudonymised', 'Indirect', 'Direct', 'Immediate'], inverse: false },
  { id: 'vulnerability_of_subjects', name: 'Vulnerability of Subjects', ticks: ['None', 'Low', 'Moderate', 'High', 'Critical'], inverse: false },
  { id: 'containment_effectiveness', name: 'Containment Effectiveness', ticks: ['None', 'Partial', 'Moderate', 'Good', 'Full'], inverse: true },
];

// ══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ══════════════════════════════════════════════════════════════════════════════
export default function BreachWorkflowPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user, isComplianceReadOnly: isReadOnly } = useAuth();
  const isDpoRole = ['dpo', 'super_admin', 'org_owner'].includes(user?.role);

  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [entities, setEntities] = useState([]);
  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [riskResult, setRiskResult] = useState(null);
  const [overrideMode, setOverrideMode] = useState(false);
  const [stepErrors, setStepErrors] = useState({});
  const [taskPromptOpen, setTaskPromptOpen] = useState(false);
  const [taskPromptBreachId, setTaskPromptBreachId] = useState(null);
  const autoSaveTimer = useRef(null);
  const reportIdRef = useRef(id || null);

  // ── Data Loading ────────────────────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      try {
        const [entitiesRes] = await Promise.all([entitiesAPI.getAll()]);
        setEntities(entitiesRes.data);
        if (id) {
          const res = await breachReportsAPI.getOne(id);
          setFormData(res.data);
          setCurrentStep(res.data.current_step || 1);
        }
      } catch { toast.error('Failed to load data'); }
      finally { setLoading(false); }
    };
    load();
  }, [id]);

  const updateField = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  // ── Computed scoring values ─────────────────────────────────────────────
  const pointsBreakdown = useMemo(() =>
    computePointsBreakdown(formData.data_categories, formData.impact_areas),
    [formData.data_categories, formData.impact_areas]
  );
  const normScores = useMemo(() => normalisePoints(pointsBreakdown), [pointsBreakdown]);
  const volScore = useMemo(() => volumeScore(formData.number_affected), [formData.number_affected]);
  const contScore = useMemo(() => containmentScore(formData.containment_measures.length), [formData.containment_measures]);

  // Auto-populate fuzzy slider values
  useEffect(() => {
    const hasSelections = formData.data_categories.length > 0 || formData.impact_areas.length > 0
      || formData.number_affected > 0 || formData.containment_measures.length > 0;
    if (!hasSelections) return;
    setFormData(prev => ({ ...prev, risk_input_values: [normScores.data_sensitivity, volScore, normScores.identification, normScores.vulnerability, contScore] }));
  }, [normScores, volScore, contScore, formData.data_categories.length, formData.impact_areas.length, formData.number_affected, formData.containment_measures.length]);

  // S-09: Auto-increment Vulnerability slider when Minors or Vulnerable Adults selected
  useEffect(() => {
    const hasMinorsOrVulnerable = formData.who_affected?.includes('Minors (Under 18)') || formData.who_affected?.includes('Vulnerable Adults');
    if (hasMinorsOrVulnerable) {
      // Set vulnerability slider to at least 60 (high vulnerability)
      setFormData(prev => {
        const currentVulnerability = prev.risk_input_values?.[3] || 0;
        if (currentVulnerability < 60) {
          const updated = [...(prev.risk_input_values || [])];
          updated[3] = 60;
          return { ...prev, risk_input_values: updated };
        }
        return prev;
      });
    }
  }, [formData.who_affected]);

  // ── Auto-save ───────────────────────────────────────────────────────────
  const saveReport = useCallback(async (data) => {
    try {
      setSaving(true);
      // Map risk levels to backend enum before saving
      const mapRiskLevel = (level) => {
        const mapping = {
          'Low': 'low',
          'low': 'low',
          'Medium': 'moderate',
          'moderate': 'moderate',
          'High': 'high',
          'high': 'high',
          'Critical': 'extreme',
          'extreme': 'extreme',
        };
        return mapping[level] || level;
      };
      const payload = {
        ...data,
        current_step: currentStep,
        risk_level: data.risk_level ? mapRiskLevel(data.risk_level) : null,
        final_risk_level: data.final_risk_level ? mapRiskLevel(data.final_risk_level) : null,
      };
      if (reportIdRef.current) {
        await breachReportsAPI.update(reportIdRef.current, payload);
      } else {
        const res = await breachReportsAPI.create(payload);
        reportIdRef.current = res.data.id;
        // Auto-fill breach_reference from backend if not already set
        if (!data.breach_reference && res.data.breach_reference) {
          setFormData(prev => ({ ...prev, breach_reference: res.data.breach_reference }));
        }
        window.history.replaceState(null, '', `/compliance/breaches/workflow/${res.data.id}`);
      }
    } catch (err) { console.error('Auto-save failed:', err); }
    finally { setSaving(false); }
  }, [currentStep]);

  useEffect(() => {
    if (loading || !formData.entity_id) return;
    // Only auto-save if current step passes validation
    const stepErrors = validateStep(currentStep, formData);
    if (Object.keys(stepErrors).length > 0) return;
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(() => saveReport(formData), 2000);
    return () => { if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current); };
  }, [formData, loading, saveReport, currentStep]);

  // ── Fuzzy risk assessment ───────────────────────────────────────────────
  useEffect(() => {
    if (currentStep !== 6 || !formData.risk_input_values?.length) return;
    const run = async () => {
      try {
        const res = await api.post('/risk-settings/assess', { configId: 'breach_risk', inputValues: formData.risk_input_values });
        setRiskResult(res.data);
        const factors = res.data.factors || [];
        const getCentroid = (vid) => factors.find(f => f.variableId === vid)?.centroid || 0;
        const sevRaw = getCentroid('data_sensitivity') * 0.43 + getCentroid('ease_of_identification') * 0.29 + getCentroid('vulnerability_of_subjects') * 0.28;
        const likRaw = getCentroid('volume_of_records') * 0.50 + getCentroid('containment_effectiveness') * 0.50;
        const sevLevel = rawToLevel(sevRaw);
        const likLevel = rawToLevel(likRaw);
        const matrixScore = sevLevel * likLevel;
        const band = getRiskBand(matrixScore);
        setFormData(prev => ({
          ...prev,
          risk_score: res.data.score, risk_level: res.data.level, risk_label: res.data.label,
          risk_action: band.action, risk_factors: res.data.factors,
          auto_severity: sevLevel, auto_likelihood: likLevel, auto_matrix_score: matrixScore,
          final_risk_level: prev.override_severity ? getRiskBand(prev.override_severity * prev.override_likelihood).level : band.level,
          points_breakdown: pointsBreakdown,
        }));
      } catch (err) { console.error('Risk assessment failed:', err); }
    };
    const t = setTimeout(run, 300);
    return () => clearTimeout(t);
  }, [formData.risk_input_values, currentStep, pointsBreakdown]);

  // ── Validation and Navigation ───────────────────────────────────────────
  const validateCurrentStep = () => {
    const errors = validateStep(currentStep, formData);
    setStepErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const scrollToFirstError = () => {
    const errors = validateStep(currentStep, formData);
    const firstErrorField = getFirstErrorFieldRef(errors);
    if (firstErrorField) {
      const element = document.querySelector(`[data-testid="${firstErrorField}"]`) ||
                      document.querySelector(`[name="${firstErrorField}"]`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        element.focus();
      }
    }
  };

  const goToStep = (step) => {
    // Validate all steps between current and destination when moving FORWARD
    // This prevents skipping steps with required fields (e.g., Step 4 → Step 6, skipping Step 5)
    // Allow free backward navigation for reviewing/fixing previous steps
    if (step > currentStep) {
      // Validate current step first
      const errors = validateStep(currentStep, formData);
      if (Object.keys(errors).length > 0) {
        setStepErrors(errors);
        scrollToFirstError();
        toast.error(`Please fill required fields on Step ${currentStep}`);
        return;
      }
      // Then validate all steps in between (if jumping multiple steps)
      for (let s = currentStep + 1; s < step; s++) {
        const stepErrors = validateStep(s, formData);
        if (Object.keys(stepErrors).length > 0) {
          setStepErrors(stepErrors);
          setCurrentStep(s);
          scrollToFirstError();
          toast.error(`Please fill required fields on Step ${s}`);
          return;
        }
      }
    }
    setCompletedSteps(prev => new Set([...prev, currentStep]));
    setCurrentStep(step);
    setStepErrors({});
  };

  const nextStep = () => {
    if (currentStep >= 9) return;
    if (validateCurrentStep()) {
      goToStep(currentStep + 1);
    } else {
      scrollToFirstError();
      toast.error(`Please fix the errors on this step (${Object.keys(stepErrors).length} field(s))`);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      goToStep(currentStep - 1);
    }
  };

  const handleSaveDraft = async () => {
    try {
      setSaving(true);
      const payload = { ...formData, current_step: currentStep, status: 'draft' };
      if (reportIdRef.current) await breachReportsAPI.update(reportIdRef.current, payload);
      else { const res = await breachReportsAPI.create(payload); reportIdRef.current = res.data.id; }
      toast.success('Saved as draft');
    } catch (err) {
      console.error('Save draft error:', err);
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    // Validate final step before submit
    if (!validateCurrentStep()) {
      scrollToFirstError();
      toast.error(`Please fix the errors on this step (${Object.keys(stepErrors).length} field(s))`);
      return;
    }

    try {
      setSaving(true);
      // Map risk levels to backend enum before submission
      const mapRiskLevel = (level) => {
        const mapping = {
          'Low': 'low',
          'low': 'low',
          'Medium': 'moderate',
          'moderate': 'moderate',
          'High': 'high',
          'high': 'high',
          'Critical': 'extreme',
          'extreme': 'extreme',
        };
        return mapping[level] || level;
      };
      const payload = {
        ...formData,
        current_step: 9,
        status: 'submitted',
        risk_level: formData.risk_level ? mapRiskLevel(formData.risk_level) : null,
        final_risk_level: formData.final_risk_level ? mapRiskLevel(formData.final_risk_level) : null,
      };
      let submittedId = reportIdRef.current;
      if (reportIdRef.current) {
        await breachReportsAPI.update(reportIdRef.current, payload);
      } else {
        const res = await breachReportsAPI.create(payload);
        submittedId = res.data.id;
        reportIdRef.current = submittedId;
      }
      toast.success('Breach report submitted');

      // Spec Section 5: offer auto-generated task bundle on final submission (DPO only)
      if (isDpoRole && submittedId) {
        setTaskPromptBreachId(submittedId);
        setTaskPromptOpen(true);
      } else {
        navigate('/compliance/breaches');
      }
    } catch (err) {
      console.error('Submit error:', err);
      let errorMsg = 'Failed to submit';
      if (err.response?.data?.detail) {
        const detail = err.response.data.detail;
        if (Array.isArray(detail)) {
          errorMsg = detail.map(d => d.msg || d.message || d).join(', ');
        } else if (typeof detail === 'object') {
          errorMsg = Object.entries(detail).map(([k, v]) => `${k}: ${v}`).join(', ');
        } else {
          errorMsg = detail;
        }
      } else if (err.message) {
        errorMsg = err.message;
      }
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const handleOverrideCell = (sev, lik) => {
    setFormData(prev => ({
      ...prev,
      override_severity: sev,
      override_likelihood: lik,
      override_by: user?.name || user?.email || '',
      override_at: new Date().toISOString(),
      final_risk_level: getRiskBand(sev * lik).level
    }));
  };
  const resetOverride = () => {
    setFormData(prev => ({ ...prev, override_severity: null, override_likelihood: null, override_justification: '', final_risk_level: prev.auto_matrix_score ? getRiskBand(prev.auto_matrix_score).level : null }));
    setOverrideMode(false);
  };

  // ── Jurisdiction logic ──────────────────────────────────────────────────
  const selectedEntity = entities.find(e => e.id === formData.entity_id);
  const orgName = selectedEntity?.entity_name || '';
  const entityJurisdiction = selectedEntity?.country || selectedEntity?.jurisdiction || '';

  // Resolve jurisdiction rules using the entity's free-text country/jurisdiction
  const entityRules = useMemo(() => getJurisdictionRules(entityJurisdiction), [entityJurisdiction]);
  const mappedJurisdiction = entityRules?.name || '';

  const notificationRequirement = useMemo(() => {
    if (!mappedJurisdiction || !formData.final_risk_level) return null;
    return getNotificationRequirement(mappedJurisdiction, formData.final_risk_level);
  }, [mappedJurisdiction, formData.final_risk_level]);

  // Sync jurisdiction fields from entity when entity changes
  useEffect(() => {
    if (!entityRules) return;
    const deadlineAt = formData.awareness_date
      ? (() => {
          const d = new Date(formData.awareness_date);
          d.setHours(d.getHours() + (entityRules.deadlineDays * 24));
          return d.toISOString();
        })()
      : null;
    setFormData(prev => {
      if (prev.jurisdiction === entityRules.name && prev.jurisdiction_code === entityRules.code) return prev;
      return {
        ...prev,
        jurisdiction: entityRules.name,
        jurisdiction_code: entityRules.code,
        supervisory_authority: entityRules.authority,
        suggested_deadline_days: entityRules.deadlineDays,
        dpo_discretion_applied: entityRules.dpo_discretion,
        ...(deadlineAt && !prev.confirmed_deadline ? { deadline_at: deadlineAt } : {}),
      };
    });
  }, [entityRules, formData.awareness_date, formData.confirmed_deadline]);

  // Update notification_required when risk is assessed
  useEffect(() => {
    if (notificationRequirement && formData.awareness_date && !formData.confirmed_deadline) {
      setFormData(prev => ({ ...prev, notification_required: notificationRequirement.mustReport, notification_requirement_reason: notificationRequirement.reportReason, suggested_deadline_days: notificationRequirement.suggestedDeadline }));
    }
  }, [notificationRequirement, formData.awareness_date, formData.confirmed_deadline]);

  const progress = Math.round((currentStep / 9) * 100);

  if (loading) return (<div className="flex items-center justify-center h-screen"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>);

  // ── Step Renderer ───────────────────────────────────────────────────────
  const renderCurrentStep = () => {
    const common = { formData, updateField, errors: stepErrors, onValidationChange: setStepErrors };
    switch (currentStep) {
      case 1: return <Step1InitialReport {...common} entities={entities} />;
      case 2: return <Step2Classification {...common} />;
      case 3: return <Step3RootCause {...common} />;
      case 4: return <Step4DataAffected {...common} pointsBreakdown={pointsBreakdown} volScore={volScore} />;
      case 5: return <Step5Response {...common} contScore={contScore} />;
      case 6: return <Step6RiskAssessment {...common} riskResult={riskResult} overrideMode={overrideMode} setOverrideMode={setOverrideMode} handleOverrideCell={handleOverrideCell} resetOverride={resetOverride} RISK_VARIABLES={RISK_VARIABLES} />;
      case 7: return <Step7Notifications {...common} notificationRequirement={notificationRequirement} user={user} selectedEntity={selectedEntity} />;
      case 8: return <Step8Remediation {...common} />;
      case 9: return (
        <>
          <Step9Review {...common} goToStep={goToStep} handleSubmit={handleSubmit} saving={saving} pointsBreakdown={pointsBreakdown} volScore={volScore} contScore={contScore} user={user} />
          {reportIdRef.current && (
            <div className="mt-6">
              <TasksSection sourceModule="breach" sourceId={reportIdRef.current} clientId={formData.client_id} entityId={formData.entity_id} title="Linked tasks" />
            </div>
          )}
        </>
      );
      default: return null;
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────
  return (
    <>
    <AutoTaskPromptModal
      open={taskPromptOpen}
      onOpenChange={(open) => {
        setTaskPromptOpen(open);
        if (!open) navigate('/compliance/breaches');
      }}
      sourceModule="breach"
      sourceId={taskPromptBreachId}
    />
    <div className="min-h-screen bg-background flex flex-col">
      <WorkflowHeader
        title={formData.title || 'New Breach Report'}
        subtitle={orgName}
        onBack={() => navigate('/compliance/breaches')}
        progressPercentage={progress}
      >
        <div className="flex flex-col items-end gap-1">
          <CountdownTimer awarenessDate={formData.awareness_date} confirmedDeadline={formData.confirmed_deadline} jurisdiction={formData.jurisdiction} deadlineDays={JURISDICTION_BREACH_RULES[formData.jurisdiction]?.deadlineDays} isDpoRole={isDpoRole} />
          <BreachOwnershipStatus dpoDecision={formData.dpo_reporting_decision} isDpoRole={isDpoRole} />
        </div>
      </WorkflowHeader>
      <div className="flex">
        {/* Left Sidebar - Stepper */}
        <div className="hidden lg:block w-64 border-r bg-muted/30 p-4 sticky top-[73px] h-[calc(100vh-73px)] overflow-y-auto">
          <div className="mb-6">
            <StepperProgress current={currentStep} total={9} />
          </div>
          <FormStepper steps={STEPS} currentStep={currentStep} completedSteps={completedSteps} onStepClick={goToStep} />
        </div>

        {/* Main Content */}
        <div className="flex-1 max-w-4xl mx-auto px-4 sm:px-6 py-6">
          <div>{renderCurrentStep()}</div>
          <div className="flex justify-between mt-8 pb-8">
            <Button variant="outline" onClick={prevStep} disabled={currentStep === 1} data-testid="prev-step"><ArrowLeft className="h-4 w-4 mr-2" />Previous</Button>
            <div className="flex gap-2">
              {reportIdRef.current && (
                <Button variant="outline" onClick={async () => {
                  try {
                    const res = await exportAPI.breachReportDocx(reportIdRef.current);
                    const url = window.URL.createObjectURL(new Blob([res.data]));
                    const a = document.createElement('a'); a.href = url;
                    a.download = res.headers['content-disposition']?.split('filename=')[1]?.replace(/"/g, '') || 'Breach_Report.docx';
                    document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
                    toast.success('Exported');
                  } catch { toast.error('Export failed'); }
                }} data-testid="export-breach-docx"><FileDown className="h-4 w-4 mr-2" />Export DOCX</Button>
              )}
              {!isReadOnly && (
                <Button variant="outline" onClick={handleSaveDraft} disabled={saving} data-testid="save-draft"><Save className="h-4 w-4 mr-2" />{saving ? 'Saving...' : 'Save Draft'}</Button>
              )}
              {currentStep < 9 && (<Button onClick={nextStep} data-testid="next-step">Next<ArrowRight className="h-4 w-4 ml-2" /></Button>)}
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
}
